import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, X, Mic, Volume2, ShoppingBasket, Sparkles } from "lucide-react";

/**
 * Scripted "PRUEBA" demo — a simulated dialog between a Mexican shopkeeper and Chalán.
 * Zero backend calls, zero mic access — pure client-side timeline using the browser's
 * SpeechSynthesis API so any prospective merchant can hear what Chalán sounds like.
 *
 * Voice roles:
 *   - "user" (Don Beto, dueño) — deeper, slightly slower voice
 *   - "chalan" (asistente) — brighter, slightly faster
 */

const SCRIPT = [
  {
    role: "user", name: "Don Beto",
    audio: "/audio/demo/01_beto_intro.mp3",
    text: "Chalán, échame dos cocas grandes y una caguama Victoria.",
    delayBefore: 300, cart: null,
  },
  {
    role: "chalan", name: "Chalán",
    audio: "/audio/demo/02_chalan_ack1.mp3",
    text: "Listo, jefe. Dos Coca Cola de dos litros y una Victoria caguama de un litro.",
    delayBefore: 400,
    cart: [
      { name: "Coca Cola 2 L", qty: 2, price: 48 },
      { name: "Victoria Caguama 1 L", qty: 1, price: 42 },
    ],
  },
  {
    role: "user", name: "Don Beto",
    audio: "/audio/demo/03_beto_more.mp3",
    text: "Súmale un Bimbo blanco y dos Marías Gamesa.",
    delayBefore: 500, cart: null,
  },
  {
    role: "chalan", name: "Chalán",
    audio: "/audio/demo/04_chalan_total.mp3",
    text: "Va, un pan Bimbo blanco grande y dos paquetes de Marías. El total de la compra son doscientos cuatro pesos, jefe.",
    delayBefore: 500,
    cart: [
      { name: "Coca Cola 2 L", qty: 2, price: 48 },
      { name: "Victoria Caguama 1 L", qty: 1, price: 42 },
      { name: "Pan Bimbo Blanco Grande", qty: 1, price: 42 },
      { name: "Galletas Marías Gamesa", qty: 2, price: 12 },
    ],
  },
  {
    role: "user", name: "Don Beto",
    audio: "/audio/demo/05_beto_pay.mp3",
    text: "Me paga con quinientos.",
    delayBefore: 500, cart: null,
  },
  {
    role: "chalan", name: "Chalán",
    audio: "/audio/demo/06_chalan_change.mp3",
    text: "Su cambio son doscientos noventa y seis pesos. Ticket generado, listo para el siguiente.",
    delayBefore: 500, cart: null, change: 296,
  },
];

const TOTAL = 204;

function useMxVoices() {
  // Kept for backwards-compat; no longer used now that we play prerecorded MP3.
  return { chalanVoice: null, userVoice: null };
}

export default function DemoChalanModal({ open, onClose }) {
  const [step, setStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [cart, setCart] = useState([]);
  const [change, setChange] = useState(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const stopAll = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch { /* noop */ }
    }
  };

  useEffect(() => () => stopAll(), []);

  useEffect(() => {
    if (!open) { stopAll(); setStep(-1); setPlaying(false); setTranscript([]); setCart([]); setChange(null); }
  }, [open]);

  const speakLine = (line, onDone) => {
    if (!line.audio) { onDone(); return; }
    const a = audioRef.current;
    if (!a) { onDone(); return; }
    const cleanup = () => {
      a.removeEventListener("ended", handleEnded);
      a.removeEventListener("error", handleError);
    };
    const handleEnded = () => { cleanup(); onDone(); };
    const handleError = () => { cleanup(); onDone(); };
    a.addEventListener("ended", handleEnded);
    a.addEventListener("error", handleError);
    a.src = line.audio;
    a.playbackRate = 1.0;
    const p = a.play();
    if (p && p.catch) p.catch(() => { cleanup(); onDone(); });
  };

  const runStep = (idx) => {
    if (idx >= SCRIPT.length) {
      setPlaying(false);
      return;
    }
    const line = SCRIPT[idx];
    timerRef.current = setTimeout(() => {
      setStep(idx);
      setTranscript((prev) => [...prev, { ...line, at: Date.now() }]);
      if (line.cart) setCart(line.cart);
      if (line.change !== undefined) setChange(line.change);
      speakLine(line, () => {
        // 700ms breath between turns
        timerRef.current = setTimeout(() => runStep(idx + 1), 700);
      });
    }, line.delayBefore || 300);
  };

  const start = () => {
    stopAll();
    setStep(-1); setTranscript([]); setCart([]); setChange(null);
    setPlaying(true);
    runStep(0);
  };
  const pause = () => { stopAll(); setPlaying(false); };
  const reset = () => { stopAll(); setStep(-1); setTranscript([]); setCart([]); setChange(null); setPlaying(false); };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="demo-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 22 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b0b0b] via-[#0a0a0a] to-[#0d0605] shadow-2xl"
        >
          {/* Hidden playback element — sources swap per line */}
          <audio ref={audioRef} preload="auto" data-testid="demo-audio" />

          {/* Grain + glows */}
          <div className="absolute inset-0 bg-grid-lines opacity-20 pointer-events-none" />
          <div className="glow-orange" style={{ top: "-20%", left: "-15%", opacity: 0.4 }} />
          <div className="glow-lime" style={{ bottom: "-25%", right: "-15%", opacity: 0.35 }} />

          {/* Header */}
          <div className="relative px-6 md:px-8 py-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#ff5a00] animate-pulse" />
              <div>
                <p className="font-mono text-[9px] tracking-widest text-white/40 uppercase mb-1">
                  / Prueba · Demostración con audio real
                </p>
                <h2 className="font-satoshi text-xl md:text-2xl leading-none">
                  Así suena <span className="text-[#CCFF00]">Chalán</span> en una tienda de verdad
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white transition"
              data-testid="demo-close"
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid md:grid-cols-5 gap-0 max-h-[calc(90vh-140px)] overflow-hidden">
            {/* Left: conversation */}
            <div className="md:col-span-3 border-r border-white/5 overflow-y-auto p-6 md:p-7 space-y-4" data-testid="demo-transcript">
              <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2 flex items-center gap-2">
                <Volume2 size={12} /> Diálogo · Don Beto (dueño) ↔ Chalán
              </p>
              {transcript.length === 0 && !playing && (
                <div className="rounded-2xl border border-dashed border-[#CCFF00]/30 p-8 text-center bg-[#CCFF00]/[0.02]">
                  <Mic size={28} className="mx-auto mb-3 text-[#CCFF00]" />
                  <p className="text-white/80 text-sm">
                    Toca <strong className="text-[#CCFF00]">Escuchar demo</strong> y sube el volumen.
                  </p>
                  <p className="text-white/50 text-xs mt-2">
                    Voz humana grabada en México · No es sintética del navegador.
                  </p>
                </div>
              )}
              {transcript.map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: line.role === "user" ? -12 : 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex ${line.role === "user" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 border ${
                      line.role === "user"
                        ? "border-white/10 bg-white/[0.03]"
                        : "border-[#CCFF00]/30 bg-[#CCFF00]/[0.05]"
                    } ${i === step ? "ring-1 ring-[#CCFF00]/40" : ""}`}
                  >
                    <p className={`font-mono text-[9px] tracking-widest uppercase mb-1 ${
                      line.role === "user" ? "text-white/40" : "text-[#CCFF00]/80"
                    }`}>
                      {line.name}
                    </p>
                    <p className="text-white text-sm leading-relaxed">{line.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Right: live ticket */}
            <div className="md:col-span-2 p-6 md:p-7 bg-black/40 flex flex-col">
              <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-3 flex items-center gap-2">
                <ShoppingBasket size={12} /> Ticket en tiempo real
              </p>
              <div className="flex-1 rounded-2xl border border-white/10 bg-white/[0.02] p-4 min-h-[240px]">
                {cart.length === 0 && (
                  <p className="text-white/30 text-xs font-mono uppercase tracking-widest text-center mt-16">
                    · esperando al cliente ·
                  </p>
                )}
                {cart.map((item, i) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex justify-between items-baseline py-2 border-b border-white/5 last:border-none"
                  >
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">{item.name}</p>
                      <p className="font-mono text-[9px] text-white/40 uppercase tracking-widest">
                        {item.qty} × ${item.price}
                      </p>
                    </div>
                    <p className="font-satoshi text-sm text-[#CCFF00]">
                      ${(item.qty * item.price).toFixed(0)}
                    </p>
                  </motion.div>
                ))}
                {cart.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/15 flex justify-between items-baseline">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-white/60">Total</span>
                    <span className="font-satoshi text-2xl text-white" data-testid="demo-total">${TOTAL}</span>
                  </div>
                )}
                {change !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 rounded-lg bg-[#CCFF00]/[0.08] border border-[#CCFF00]/30 px-3 py-2 flex justify-between items-baseline"
                  >
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[#CCFF00]">Cambio</span>
                    <span className="font-satoshi text-xl text-[#CCFF00]" data-testid="demo-change">${change}</span>
                  </motion.div>
                )}
              </div>
              <p className="mt-4 text-white/40 text-[11px] leading-relaxed">
                <Sparkles size={11} className="inline text-[#CCFF00] mr-1" />
                Chalán entiende marcas mexicanas, calcula el total en pesos hablados y arma el ticket sin tocar la pantalla.
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="relative px-6 md:px-8 py-4 border-t border-white/10 bg-black/40 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {!playing ? (
                <button
                  onClick={start}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest hover:bg-white transition"
                  data-testid="demo-play"
                >
                  <Play size={14} /> {transcript.length === 0 ? "Escuchar demo" : "Reproducir de nuevo"}
                </button>
              ) : (
                <button
                  onClick={pause}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-white font-mono text-xs uppercase tracking-widest border border-white/20 hover:bg-white/15"
                  data-testid="demo-pause"
                >
                  <Pause size={14} /> Pausar
                </button>
              )}
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-full border border-white/10 text-white/60 hover:text-white text-xs font-mono uppercase tracking-widest"
                data-testid="demo-reset"
                title="Reiniciar"
              >
                <RotateCcw size={13} />
              </button>
            </div>
            <a
              href="/setup"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#FF5A00]/40 bg-[#FF5A00]/10 text-[#FF5A00] font-mono text-xs uppercase tracking-widest hover:bg-[#FF5A00]/20"
              data-testid="demo-cta-signup"
            >
              Me convenció · Registrar mi tienda
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
